import React from 'react';
import moment from 'moment-timezone';
import DateInput from 'react/components/date_input';
import Button from 'react/components/button';
import Checkbox from 'react/components/checkbox';
import RadioButtons from 'react/components/radio_buttons';
import Input from 'react/components/input';
import type { Contact } from 'react/types/contact';
import type {
  MembershipCancellation,
  MembershipWithCancellation,
  RecurringBooking,
} from 'react/types/membership_cancellation';

type CancellationStepTwoProps = {
  contact: Contact;
  membership: MembershipWithCancellation;
  currency: string;
  cancellationData: MembershipCancellation;
  cancellationFeePresent: boolean;
  recurringBookings: RecurringBooking[];
  errors: {
    cancellation?: { cancellation_fee?: string[]; cancellation_fee_due_on?: string[] };
    membership_change_during_payment_generation_error?: boolean;
  };
  feeMinDate: Date;
  maxDate: Date;
  onCancellationDataChange: (data: MembershipCancellation) => void;
  onCancellationFeeToggle: (checked: boolean) => void;
  onDeactivateRecurringBooking: (booking: RecurringBooking) => void;
  onSave: () => void;
  onBack: () => void;
  isSecondStepReady: boolean;
  isCancellationInThePast: boolean;
};

const CancellationStepTwo = (props: CancellationStepTwoProps) => {
  const {
    contact,
    membership,
    currency,
    cancellationData,
    cancellationFeePresent,
    recurringBookings,
    errors,
    feeMinDate,
    maxDate,
    onCancellationDataChange,
    onCancellationFeeToggle,
    onDeactivateRecurringBooking,
    onSave,
    onBack,
    isSecondStepReady,
    isCancellationInThePast,
  } = props;

  const paymentOptions = [
    {
      value: 'delete_all_payments',
      label: !isCancellationInThePast
        ? `Delete all payments between now and cancellation date (${moment(
            cancellationData.cancel_on,
          ).format('MMM D, YYYY')})`
        : 'Cancellation date is in the past: all future payments will be removed.',
    },
    ...(!isCancellationInThePast
      ? [
          {
            value: 'delete_payments_after_cancellation',
            label: `Let payments continue until cancellation date (${moment(
              cancellationData.cancel_on,
            ).format('MMM D, YYYY')})`,
          },
        ]
      : []),
  ];

  return (
    <main className="sub-content">
      <div className="content-box">
        <div className="row">
          <div className="col-sm-8">
            <div className="form-group">
              <h4>
                {contact.name} - {membership.label}
              </h4>
              <p>
                <strong>
                  Please choose from the options below, regarding this membership's payments:
                </strong>
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            {(membership.cost_repeat || membership.cost_upfront) && (
              <div className="form-group">
                <RadioButtons
                  options={paymentOptions}
                  value={cancellationData.change_payments}
                  onChange={(value) =>
                    onCancellationDataChange({
                      ...cancellationData,
                      change_payments: value,
                    })
                  }
                  getLabel={(option) => option.label}
                  getValue={(option) => option.value}
                  vertical
                />
              </div>
            )}

            <div className="form-group">
              <Checkbox checked={cancellationFeePresent} onChange={onCancellationFeeToggle}>
                Add cancellation fee
              </Checkbox>
            </div>

            {cancellationFeePresent && (
              <div className="form-group">
                <div className="inline-modules-with-notes">
                  <div className="inline-label inline-label--compact">Collect</div>
                  <div className="inline-module no-margin is-narrow">
                    <div className="input-group is-sm group-with-addon">
                      <span className="input-group-addon">{currency}</span>
                      <Input
                        name="cancellation_fee"
                        type="number"
                        min={0}
                        value={cancellationData.cancellation_fee ?? ''}
                        onChange={(value) => {
                          const strValue = String(value);
                          const numValue = strValue === '' ? undefined : parseFloat(strValue);
                          onCancellationDataChange({
                            ...cancellationData,
                            cancellation_fee: numValue,
                            cancellation_fee_due_on:
                              numValue && numValue > 0 ? cancellationData.cancellation_fee_due_on : undefined,
                          });
                        }}
                      />
                    </div>
                    {errors.cancellation?.cancellation_fee?.[0] && (
                      <div className="alert alert-danger is-sm">
                        {errors.cancellation.cancellation_fee[0]}
                      </div>
                    )}
                  </div>
                  <div className="inline-label inline-label--compact">on</div>
                  <div className="inline-module no-margin is-narrow">
                    <DateInput
                      value={
                        cancellationData.cancellation_fee_due_on
                          ? moment(cancellationData.cancellation_fee_due_on).format('YYYY-MM-DD')
                          : undefined
                      }
                      onChange={(value) =>
                        onCancellationDataChange({
                          ...cancellationData,
                          cancellation_fee_due_on: value,
                        })
                      }
                      minDate={moment(feeMinDate).format('YYYY-MM-DD')}
                      maxDate={moment(maxDate).format('YYYY-MM-DD')}
                      variant="v2"
                    />
                    {errors.cancellation?.cancellation_fee_due_on?.[0] && (
                      <div className="alert alert-danger is-sm">
                        {errors.cancellation.cancellation_fee_due_on[0]}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {errors.membership_change_during_payment_generation_error && (
              <div className="alert alert-danger">
                There was an error updating payments. Please try again.
              </div>
            )}
          </div>
        </div>

        {recurringBookings.length > 0 && (
          <>
            <hr className="is-sm" />
            <div className="margin-bottom-16">
              <strong>Deactivate recurring bookings?</strong>
            </div>
            <div className="margin-bottom-16">
              This membership is currently being used for one or more of the contact's recurring
              bookings.
            </div>
            <div className="margin-bottom-16">
              If the membership is cancelled, future events for the affected series may no longer be
              booked.
            </div>
            <div className="margin-bottom-16">
              Please review the list below, and deactivate any recurring bookings that are no longer
              needed for this contact.
            </div>
            <div className="margin-bottom-16">
              <strong>
                Note: deactivating recurring bookings will not cancel any existing bookings. It will
                only ensure that no new bookings are created.
              </strong>
            </div>

            {recurringBookings.map((booking) => (
              <div key={booking.id} className="margin-bottom-16">
                <strong>{booking.event_schedule_name}</strong>
                <div className="margin-bottom-8">
                  Enabled for events from:{' '}
                  {moment(booking.recurring_bookings_start_at).format('MMM D, YYYY')}
                </div>
                <Button
                  variant="default"
                  size="sm"
                  disabled={booking.loading || booking.deactivated}
                  onClick={() => onDeactivateRecurringBooking(booking)}
                >
                  {booking.deactivated ? 'Deactivated' : 'Deactivate'}
                </Button>
              </div>
            ))}
          </>
        )}
      </div>

      <footer className="worx-combo-box is-filled is-padded">
        <Button variant="success" disabled={!isSecondStepReady} onClick={onSave}>
          Save
        </Button>{' '}
        <Button variant="default" onClick={onBack}>
          Cancel
        </Button>
      </footer>
    </main>
  );
};

export default CancellationStepTwo;
